import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static values = {
    code: String,
    participantLabel: String,
    roomStatus: String,
    uploadUrl: String
  }

  static targets = [
    "statusA", "statusB", "nameA", "nameB", "nameBPill", "matchStatus", "uploadSection",
    "resultsSection", "map", "timeline", "noMatches", "uploadController",
    "waitingMessage"
  ]

  connect() {
    this.subscription = consumer.subscriptions.create(
      { channel: "RoomChannel", code: this.codeValue },
      {
        received: (data) => this.handleBroadcast(data)
      }
    )

    // Restore correct UI state based on server-side room status
    if (this.roomStatusValue === "complete") {
      this.showResults()
    } else if (this.roomStatusValue === "comparing") {
      // Matching is already in progress — hide waiting message, show comparing state
      if (this.hasWaitingMessageTarget) {
        this.waitingMessageTarget.classList.add("hidden")
      }
      if (this.hasMatchStatusTarget) {
        this.matchStatusTarget.innerHTML = '<span style="color: #7c3aed; font-weight: 500;">Comparing photos...</span>'
      }
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    // Don't leave the thumbnails-updated timer/resolver dangling if the controller
    // is torn down mid-wait (e.g. a Turbo visit). Clear the timeout and resolve so
    // any awaiter doesn't hang.
    if (this._thumbnailsUpdatedTimeout) {
      clearTimeout(this._thumbnailsUpdatedTimeout)
      this._thumbnailsUpdatedTimeout = null
    }
    if (this._thumbnailsUpdatedResolve) {
      this._thumbnailsUpdatedResolve()
      this._thumbnailsUpdatedResolve = null
    }
  }

  handleBroadcast(data) {
    switch (data.type) {
      case "participant_joined":
        this.onParticipantJoined(data)
        break
      case "metadata_progress":
        this.onMetadataProgress(data)
        break
      case "participant_ready":
        this.onParticipantReady(data)
        break
      case "matching_started":
        this.onMatchingStarted()
        break
      case "match_found":
        this.onMatchFound(data)
        break
      case "matching_complete":
        this.onMatchingComplete(data)
        break
      case "thumbnails_updated":
        this.onThumbnailsUpdated()
        break
    }
  }

  onParticipantJoined(data) {
    const shareSection = document.getElementById("share-section")
    if (shareSection) shareSection.classList.add("hidden")

    if (data.label === "B") {
      this.statusBTarget.textContent = "Joined"
      if (data.name) {
        if (this.hasNameBTarget) {
          this.nameBTarget.textContent = data.name
        }
        if (this.hasNameBPillTarget) {
          this.nameBPillTarget.textContent = data.name.split(" ")[0]
        }
      }
    }
  }

  onMetadataProgress(data) {
    const target = data.label === "A" ? this.statusATarget : this.statusBTarget
    target.textContent = `Uploading (${data.count} photos)`
  }

  onParticipantReady(data) {
    const target = data.label === "A" ? this.statusATarget : this.statusBTarget
    target.textContent = "Ready"
  }

  onMatchingStarted() {
    this.matchStatusTarget.innerHTML = '<span class="text-indigo-600 font-medium">Comparing photos...</span>'
    this.statusATarget.textContent = "Ready"
    this.statusBTarget.textContent = "Ready"
    if (this.hasWaitingMessageTarget) {
      this.waitingMessageTarget.classList.add("hidden")
    }
    this.showResults()
  }

  onMatchFound(data) {
    if (this.hasNoMatchesTarget) {
      this.noMatchesTarget.classList.add("hidden")
    }

    // Add match card to timeline
    this.addMatchCard(data)

    // Add marker to map
    this.addMapMarker(data)

    // Update match count
    const count = document.querySelectorAll(".match-card").length
    const label = count === 1 ? "match" : "matches"
    this.matchStatusTarget.innerHTML = `<span class="text-sm font-semibold" style="color: #16a34a;">${count} ${label} found</span>`
  }

  async onMatchingComplete(data) {
    if (data.total_matches > 0) {
      const photoLibraryEl = document.querySelector('[data-controller~="photo-library"]')
      const ctrl = photoLibraryEl
        ? this.application.getControllerForElementAndIdentifier(photoLibraryEl, "photo-library")
        : null
      const hasNativeBridge = ctrl && ctrl.bridge

      if (hasNativeBridge) {
        // iOS: run backfill now (fetches missing iCloud thumbnails), then reload.
        // The backfill POSTs to /library_photos/thumbnails which broadcasts
        // thumbnails_updated so Chrome (already reloaded) picks them up.
        try { await ctrl._backfillMatchThumbnails() } catch (e) {}
        window.location.reload()
      } else if (data.all_thumbnails_present) {
        // Web: all thumbnails already saved — reload immediately.
        window.location.reload()
      } else {
        // Web: thumbnails are missing (iOS is still fetching from iCloud).
        // Wait for thumbnails_updated broadcast before reloading, with a
        // 10-second fallback so we never get stuck.
        await new Promise(resolve => {
          this._thumbnailsUpdatedResolve = resolve
          this._thumbnailsUpdatedTimeout = setTimeout(resolve, 10000)
        })
        window.location.reload()
      }
      return
    }

    // No matches found — update UI in place so the user can add more photos.
    if (this.hasMatchStatusTarget) this.matchStatusTarget.innerHTML = ""
    if (this.hasNoMatchesTarget) {
      this.noMatchesTarget.innerHTML = `
        <p class="text-base font-medium text-gray-600">No matches found</p>
        <p class="text-sm mt-1 text-gray-400">Try adding photos from a wider date range or different location</p>
        <button data-action="click->room#addMorePhotos"
                style="margin-top:16px;background:linear-gradient(135deg,#7c3aed,#f43f5e);color:white;border:none;font-size:14px;font-weight:600;padding:10px 20px;border-radius:12px;cursor:pointer;">
          + Add more photos
        </button>
      `
      this.noMatchesTarget.classList.remove("hidden")
    }
    this.roomStatusValue = "complete"
  }

  onThumbnailsUpdated() {
    if (this._thumbnailsUpdatedResolve) {
      // We were waiting for this — resolve the promise, which triggers reload.
      clearTimeout(this._thumbnailsUpdatedTimeout)
      this._thumbnailsUpdatedResolve()
      this._thumbnailsUpdatedResolve = null
    } else {
      // Already reloaded (e.g. from a previous thumbnails_updated) — reload again.
      window.location.reload()
    }
  }

  showResults() {
    if (this.hasResultsSectionTarget) {
      this.resultsSectionTarget.classList.remove("hidden")
      // Leaflet needs a size recalc after the container becomes visible
      setTimeout(() => {
        const event = new CustomEvent("map:invalidate", { bubbles: true })
        this.element.dispatchEvent(event)
      }, 100)
    }
  }

  addMatchCard(data) {
    if (!this.hasTimelineTarget) return

    const matchYear = new Date(data.matched_on + "T00:00:00").getFullYear().toString()

    // Check if a year header already exists for this year
    let yearHeader = this.timelineTarget.querySelector(`.timeline-year[data-year="${matchYear}"]`)
    if (!yearHeader) {
      yearHeader = document.createElement("div")
      yearHeader.className = "timeline-year relative flex items-center gap-3 py-2"
      yearHeader.dataset.year = matchYear
      yearHeader.innerHTML = `
        <div class="z-10 w-3 h-3 shrink-0 rounded-full bg-indigo-600"></div>
        <span class="text-sm font-bold text-gray-800">${matchYear}</span>
        <div class="flex-1 h-px bg-gray-300"></div>
      `
      this.timelineTarget.appendChild(yearHeader)
    }

    const dateStr = new Date(data.matched_on + "T00:00:00").toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric"
    })

    const nameA = this.hasNameATarget ? this.nameATarget.textContent.trim() : "Person A"
    const nameB = this.hasNameBTarget ? this.nameBTarget.textContent.trim() : "Person B"

    const node = document.createElement("div")
    node.className = "timeline-node relative flex gap-3 pb-4"
    node.dataset.matchedOn = data.matched_on

    node.innerHTML = `
      <div class="z-10 mt-2 w-2.5 h-2.5 shrink-0 rounded-full bg-indigo-400"></div>
      <div class="match-card flex-1 border border-gray-200 rounded-lg p-4" id="match-${data.match_id}" data-match-id="${data.match_id}">
        <div class="flex items-center justify-between mb-3">
          <div>
            <span class="text-sm font-semibold text-gray-700">${dateStr}</span>
            <span class="text-xs text-gray-400 ml-2">${Math.round(data.distance_meters)}m apart</span>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <p class="text-xs text-blue-600 font-medium mb-1">${nameA}</p>
            <div class="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center"
                 id="match-${data.match_id}-photo_a">
              <span class="text-gray-400 text-sm">Awaiting upload...</span>
            </div>
          </div>
          <div>
            <p class="text-xs text-green-600 font-medium mb-1">${nameB}</p>
            <div class="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center"
                 id="match-${data.match_id}-photo_b">
              <span class="text-gray-400 text-sm">Awaiting upload...</span>
            </div>
          </div>
        </div>
      </div>
    `
    this.timelineTarget.appendChild(node)
  }

  addMapMarker(data) {
    // Dispatch event for map controller to handle
    const event = new CustomEvent("match:added", {
      detail: {
        lat: data.center_latitude,
        lng: data.center_longitude,
        date: data.matched_on,
        distance: data.distance_meters,
        matchId: data.match_id
      },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  addMorePhotos() {
    // Show upload section and hide server-rendered waiting message
    if (this.hasUploadSectionTarget) {
      this.uploadSectionTarget.classList.remove("hidden")
    }
    if (this.hasWaitingMessageTarget) {
      this.waitingMessageTarget.classList.add("hidden")
    }

    // Native: trigger photo picker directly via bridge
    const photoLibraryEl = document.querySelector('[data-controller~="photo-library"]')
    if (photoLibraryEl) {
      const ctrl = this.application.getControllerForElementAndIdentifier(photoLibraryEl, "photo-library")
      if (ctrl) {
        ctrl.addMorePhotos()
        return
      }
    }

    // Web: open file picker directly via photo-upload controller
    const photoUploadEl = document.querySelector('[data-controller~="photo-upload"]')
    if (photoUploadEl) {
      const ctrl = this.application.getControllerForElementAndIdentifier(photoUploadEl, "photo-upload")
      if (ctrl) ctrl.addMorePhotos()
    }
  }

  get isNative() {
    return typeof window !== "undefined" && window.HotwireNative !== undefined
  }
}
